/*
 * PDP V2 interactivity — canon v2 (Project 02).
 *
 * Behaviour bundle:
 *   · PDP-A1 — accordion stack: one open at a time, "What it does" open on load
 *   · PDP-S1 — subscription disclosure: quiet expand, one-time default
 *   · PDP-M1 — mobile sticky bar: expand on tap, mini-buybox inline
 *   · Pack switcher — Section Rendering API live swap, sub state persists
 *   · Gallery — hero + thumb strip, click thumb → swap hero, click any →
 *               open lightbox (arrow keys + escape)
 *   · Sticky bar — visible only when buy box leaves viewport AND Final CTA
 *                  is not yet in view.
 *
 * Idempotent — safe to re-run after Section Rendering API swap. Each init
 * checks for the dataset flag before re-binding.
 *
 * Dawn rem base: 1rem = 10px.
 */
(function () {
  'use strict';

  /* ───────────────────────────────────────────────────────────
     ACCORDIONS — one open at a time, scoped to .pdp-accordions
  ──────────────────────────────────────────────────────────── */
  function initAccordions() {
    document.querySelectorAll('.pdp-accordions').forEach(function (group) {
      if (group.dataset.accordionBound === 'true') return;
      group.addEventListener('toggle', function (e) {
        if (!e.target.open) return;
        var current = e.target;
        group.querySelectorAll('details[open]').forEach(function (d) {
          if (d === current) return;
          var wrap = d.querySelector('.pdp-accordion__body-wrap');
          if (wrap) {
            wrap.style.gridTemplateRows = '0fr';
            wrap.addEventListener('transitionend', function once() {
              d.removeAttribute('open');
              wrap.style.gridTemplateRows = '';
              wrap.removeEventListener('transitionend', once);
            });
          } else {
            d.removeAttribute('open');
          }
        });
      }, true);
      group.dataset.accordionBound = 'true';
    });
  }

  /* Apply a percentage discount to a money string, preserving its format.
     "$110" → "$99", "$398" → "$358.20", "A$1,165.00" → "A$1,048.50".
     Used to recompute the subscribe price on variant switch (the sub-price
     span is server-rendered exactly via | money for the default variant). */
  function applyPct(priceText, pct) {
    var s = String(priceText);
    var m = s.match(/^(\D*)([\d.,]+)(.*)$/);
    if (!m) return s;
    var pre = m[1], num = m[2], post = m[3];
    var hasDec = /[.,]\d{2}$/.test(num);
    var val = parseFloat(num.replace(/,/g, ''));
    if (isNaN(val)) return s;
    var disc = val * (100 - pct) / 100;
    var out = (hasDec || disc % 1 !== 0) ? disc.toFixed(2) : String(disc);
    out = out.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return pre + out + post;
  }

  /* Reflect the active purchase option (one-time vs subscribe) in BOTH the big
     price block and the ATC label, so they never disagree. The one-time price is
     stashed on the price element (data-onetime) the first time, so it survives
     the swap to the sub price and back. Module-scope so the disclosure toggle,
     the variant switcher, and boot can all call it. */
  function refreshPdpPriceUI() {
    var card = document.querySelector('[data-pdp-sub-disclose]');
    var priceEl = document.querySelector('[data-pdp-price]');
    var atcLabel = document.querySelector('[data-pdp-atc-label]');
    var atc = document.querySelector('[data-pdp-atc]');
    var isSub = !!(card && card.dataset.state === 'subscribed');
    var subEl = card && card.querySelector('[data-pdp-sub-price]');
    var subText = subEl ? subEl.textContent.trim() : '';

    if (priceEl) {
      if (!priceEl.dataset.onetime) priceEl.dataset.onetime = priceEl.textContent.trim();
      priceEl.textContent = (isSub && subText) ? subText : priceEl.dataset.onetime;
    }
    document.querySelectorAll('[data-pdp-promo-only], [data-pdp-onetime-only]').forEach(function (el) {
      el.hidden = isSub;
    });
    if (atcLabel) {
      var oneTime = priceEl ? priceEl.dataset.onetime : '';
      if (atc && atc.hasAttribute('disabled')) atcLabel.textContent = 'Sold Out';
      else if (isSub) atcLabel.textContent = 'Subscribe · ' + (subText || oneTime);
      else atcLabel.textContent = 'Add to Cart · ' + oneTime;
    }
  }

  /* ───────────────────────────────────────────────────────────
     SUBSCRIPTION DISCLOSURE CARD — PDP-S1
     One-time selected by default. Click "Switch to subscribe" → flips the
     card to subscribed state, writes the selling plan ID to the hidden form
     input, and syncs the price block + ATC label. Frequency selector appears
     inline only when subscribed; changing it updates the selling plan.
  ──────────────────────────────────────────────────────────── */
  function initSubDisclosure() {
    var card = document.querySelector('[data-pdp-sub-disclose]');
    if (!card || card.dataset.subBound === 'true') return;
    card.dataset.subBound = 'true';

    var toggle = card.querySelector('[data-pdp-sub-toggle]');
    var toggleLabel = card.querySelector('[data-pdp-sub-toggle-label]');
    var freqSelect = card.querySelector('[data-pdp-frequency]');
    var planInput = document.querySelector('[data-pdp-selling-plan]');
    var atcLabel = document.querySelector('[data-pdp-atc-label]');
    var atc = document.querySelector('[data-pdp-atc]');
    var priceEl = document.querySelector('[data-pdp-price]');
    var defaultPlanId = card.dataset.defaultPlanId;

    function currentPlanId() {
      return freqSelect ? freqSelect.value : defaultPlanId;
    }

    function setState(isSub) {
      card.dataset.state = isSub ? 'subscribed' : 'inactive';
      if (toggle) toggle.setAttribute('aria-pressed', isSub ? 'true' : 'false');
      if (toggleLabel) toggleLabel.textContent = isSub ? 'Switch to one-time' : 'Switch to subscribe';
      if (planInput) planInput.value = isSub ? currentPlanId() : '';
      refreshPdpPriceUI();
      syncStickyBar();
    }

    if (toggle) {
      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        var nextState = card.dataset.state !== 'subscribed';
        setState(nextState);
      });
    }

    if (freqSelect) {
      freqSelect.addEventListener('change', function () {
        if (card.dataset.state === 'subscribed' && planInput) {
          planInput.value = currentPlanId();
        }
        syncStickyBar();
      });
    }

    /* Submit-time guard: the disclosure card is the canonical subscribe UI, so
       re-assert OUR selling_plan at the moment of add-to-cart. The native
       Recharge widget is CSS-suppressed but its (hidden) JS may still touch the
       form's selling_plan on load — capture-phase ensures our value wins before
       Dawn's product-form serialises the form. */
    var form = document.getElementById('pdp-product-form');
    if (form && form.dataset.subGuardBound !== 'true') {
      form.dataset.subGuardBound = 'true';
      form.addEventListener('submit', function () {
        if (planInput) planInput.value = card.dataset.state === 'subscribed' ? currentPlanId() : '';
      }, true);
    }

    /* Sync at boot — preserves data-state from server render (Complete
       Maintenance ships with sub_default=true). */
    setState(card.dataset.state === 'subscribed');
  }

  /* ───────────────────────────────────────────────────────────
     VARIANT SWITCHER — D21 (2-col, same-product variants)
     For products where the SKU family is implemented as Shopify variants
     (e.g. Chloride 100ml / 700ml) rather than as separate linked products.
     Click writes the variant ID into the form's hidden id input, updates the
     price display + per-unit line, and re-syncs the sticky bar. Subscription
     state persists across switches (the disclosure card is untouched).
  ──────────────────────────────────────────────────────────── */
  /* Roving-focus arrow-key nav for a radiogroup of <button role="radio">.
     Selection does NOT follow focus (APG's expensive-selection variant): the
     pack swap is costly, so arrows only move focus — Space/Enter/click selects
     (native button activation runs the existing click handlers). */
  function radioGroupArrows(e, group, radioSel) {
    if (!group) return;
    var radios = Array.prototype.slice.call(group.querySelectorAll(radioSel));
    var idx = radios.indexOf(e.target.closest(radioSel));
    if (idx === -1) return;
    var to;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') to = (idx + 1) % radios.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') to = (idx - 1 + radios.length) % radios.length;
    else if (e.key === 'Home') to = 0;
    else if (e.key === 'End') to = radios.length - 1;
    else return;
    e.preventDefault();
    radios[to].focus();
  }

  function initVariantSwitcher() {
    var sw = document.querySelector('.pdp-variant-switcher');
    if (!sw || sw.dataset.variantBound === 'true') return;
    sw.dataset.variantBound = 'true';

    sw.addEventListener('click', function (e) {
      var btn = e.target.closest('.pdp-variant-switcher__btn');
      if (!btn || btn.classList.contains('is-current')) return;

      sw.querySelectorAll('.pdp-variant-switcher__btn').forEach(function (b) {
        b.classList.remove('is-current');
        b.setAttribute('aria-checked', 'false');
        b.setAttribute('tabindex', '-1');
      });
      btn.classList.add('is-current');
      btn.setAttribute('aria-checked', 'true');
      btn.setAttribute('tabindex', '0');

      var variantId = btn.dataset.variantId;
      var variantPrice = btn.dataset.variantPrice;

      var idInput = document.querySelector('[data-pdp-variant-id]');
      if (idInput && variantId) idInput.value = variantId;

      /* New variant: update the one-time source + recompute the discounted sub
         price, then let refreshPdpPriceUI() set the price block + ATC label per
         the current one-time/subscribe state. */
      var priceEl = document.querySelector('[data-pdp-price]');
      var card = document.querySelector('[data-pdp-sub-disclose]');
      var subEl = card && card.querySelector('[data-pdp-sub-price]');
      if (priceEl && variantPrice) priceEl.dataset.onetime = btn.dataset.variantPromoPrice || variantPrice;
      var promoRegular = document.querySelector('[data-pdp-promo-regular]');
      if (promoRegular && variantPrice) promoRegular.textContent = variantPrice;
      if (subEl && variantPrice) {
        var pct = parseFloat(card.dataset.subPct || '10');
        subEl.textContent = applyPct(variantPrice, pct);
      }
      refreshPdpPriceUI();

      /* Reflect the chosen variant in the URL without a reload */
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({ variantId: variantId }, '', url.toString());
      } catch (_) { /* no-op */ }

      syncStickyBar();
    });

    sw.addEventListener('keydown', function (e) {
      if (e.target.closest('.pdp-variant-switcher__btn')) radioGroupArrows(e, sw, '.pdp-variant-switcher__btn');
    });
  }

  /* ───────────────────────────────────────────────────────────
     PACK SWITCHER — Section Rendering API live swap
     Buys: sub vs. one-time intent persists, current price + sticky bar update.
  ──────────────────────────────────────────────────────────── */
  function initPackSwitcher() {
    var hero = document.querySelector('.pdp-hero');
    if (!hero || hero.dataset.packBound === 'true') return;
    hero.dataset.packBound = 'true';
    var sectionId = hero.dataset.sectionId;

    hero.addEventListener('click', function (e) {
      var tab = e.target.closest('.pdp-pack-tab');
      if (!tab || tab.classList.contains('pdp-pack-tab--active')) return;
      handlePackSwitch(tab, hero, sectionId);
    });

    hero.addEventListener('keydown', function (e) {
      var tab = e.target.closest('.pdp-pack-tab');
      if (tab) radioGroupArrows(e, tab.closest('.pdp-pack-switcher'), '.pdp-pack-tab');
    });

    window.addEventListener('popstate', function () { window.location.reload(); });
  }

  async function handlePackSwitch(tab, hero, sectionId) {
    var handle = tab.dataset.packHandle;
    var productUrl = tab.dataset.packUrl;
    if (!handle || !productUrl) return;

    /* Capture pre-swap state */
    var prevCard = hero.querySelector('[data-pdp-sub-disclose]');
    var prevSubscribed = prevCard ? prevCard.dataset.state === 'subscribed' : false;

    hero.classList.add('pdp-hero--loading');
    hero.querySelectorAll('.pdp-pack-tab').forEach(function (t) {
      t.classList.remove('pdp-pack-tab--active');
      t.setAttribute('aria-checked', 'false');
      t.setAttribute('tabindex', '-1');
    });
    tab.classList.add('pdp-pack-tab--active');
    tab.setAttribute('aria-checked', 'true');
    tab.setAttribute('tabindex', '0');

    try {
      var res = await fetch(productUrl + '?sections=' + sectionId);
      if (!res.ok) throw new Error('Fetch ' + res.status);
      var data = await res.json();
      var html = data[sectionId];
      if (!html) throw new Error('Section not in response');

      var doc = new DOMParser().parseFromString(html, 'text/html');
      var newHero = doc.querySelector('.pdp-hero');
      if (!newHero) throw new Error('No .pdp-hero in returned HTML');

      /* Swap gallery */
      var newGallery = newHero.querySelector('.pdp-gallery');
      var oldGallery = hero.querySelector('.pdp-gallery');
      if (newGallery && oldGallery) oldGallery.replaceWith(newGallery);

      /* Swap buy box — but keep the pack switcher with its updated active state */
      var newBuybox = newHero.querySelector('.pdp-buybox');
      var oldBuybox = hero.querySelector('.pdp-buybox');
      if (newBuybox && oldBuybox) {
        var currentSwitcher = oldBuybox.querySelector('.pdp-pack-switcher');
        oldBuybox.replaceWith(newBuybox);
        var newSwitcher = newBuybox.querySelector('.pdp-pack-switcher');
        if (currentSwitcher && newSwitcher) newSwitcher.replaceWith(currentSwitcher);
        refreshPackPrices(newBuybox);
      }

      /* Re-init behaviour on the fresh DOM */
      initAccordions();
      initSubDisclosure();
      initGalleryHeroSwap();
      initLightbox();
      initStickyBar();

      /* Persist subscribe intent across pack swap */
      if (prevSubscribed) {
        var newCard = hero.querySelector('[data-pdp-sub-disclose]');
        if (newCard) {
          var newToggle = newCard.querySelector('[data-pdp-sub-toggle]');
          if (newToggle && newCard.dataset.state !== 'subscribed') newToggle.click();
        }
      }

      /* URL bar update — no reload */
      window.history.pushState({ handle: handle }, '', productUrl);

      /* Sticky bar mirrors fresh state */
      syncStickyBar();
    } catch (err) {
      /* Hard-navigate fallback */
      console.error('Pack switch failed, navigating:', err);
      window.location.href = productUrl;
    } finally {
      hero.classList.remove('pdp-hero--loading');
    }
  }

  /* Retained comparisons follow the returned pack's selected one-time price. */
  function refreshPackPrices(buybox) {
    var handle = buybox.dataset.pdpPackHandle;
    if (!handle) return;
    var unit = buybox.dataset.pdpPackUnitPrice || '';
    var total = buybox.dataset.pdpPackTotalPrice || '';

    function updateMoney(row, className, money, before, suffix) {
      var node = row.querySelector('.' + className);
      if (!money) {
        if (node) node.remove();
        return;
      }
      if (!node) {
        node = document.createElement('span');
        node.className = className;
        row.insertBefore(node, before);
      }
      // Only Liquid-escaped, server-formatted money fragments cross this boundary.
      node.innerHTML = money;
      if (suffix) node.appendChild(document.createTextNode(suffix));
    }

    buybox.querySelectorAll('.pdp-pack-tab').forEach(function (row) {
      if (row.dataset.packHandle !== handle) return;
      var size = row.querySelector('.pdp-pack-tab__size');
      updateMoney(row, 'pdp-pack-tab__price', unit, size ? size.nextSibling : null, ' per sachet');
    });
    document.querySelectorAll('.pdp-pack-ladder__col').forEach(function (row) {
      if (row.dataset.ladderPack !== handle) return;
      var count = row.querySelector('.pdp-pack-ladder__count');
      var label = row.querySelector('.pdp-pack-ladder__per-label');
      if (unit && !label) {
        label = document.createElement('span');
        label.className = 'pdp-pack-ladder__per-label';
        label.textContent = 'per sachet';
        row.insertBefore(label, count);
      } else if (!unit && label) {
        label.remove();
        label = null;
      }
      updateMoney(row, 'pdp-pack-ladder__per-num', unit, label || count);
      updateMoney(row, 'pdp-pack-ladder__total', total, count ? count.nextSibling : null);
    });
  }

  /* ───────────────────────────────────────────────────────────
     GALLERY — hero + thumbs, click thumb to swap hero
  ──────────────────────────────────────────────────────────── */
  function initGalleryHeroSwap() {
    var gallery = document.querySelector('.pdp-gallery');
    if (!gallery || gallery.dataset.heroBound === 'true') return;
    gallery.dataset.heroBound = 'true';

    gallery.addEventListener('click', function (e) {
      var thumb = e.target.closest('.pdp-gallery__thumb');
      if (!thumb) return;
      var idx = parseInt(thumb.dataset.thumbIndex, 10);
      gallery.querySelectorAll('.pdp-gallery__thumb').forEach(function (t, i) {
        t.classList.toggle('is-active', i === idx);
      });
      var hero = gallery.querySelector('#pdp-gallery-hero');
      if (!hero) return;
      var thumbContent = thumb.querySelector('img, .pdp-gallery__placeholder');
      var heroContent = hero.querySelector('img, .pdp-gallery__placeholder');
      if (thumbContent && heroContent) {
        var clone = thumbContent.cloneNode(true);
        if (clone.tagName === 'IMG') {
          /* Upgrade the src to a larger width for the hero slot (Shopify zoomable standard) */
          var src = clone.getAttribute('src') || '';
          clone.setAttribute('src', src.replace(/width=\d+/, 'width=2048'));
          var srcset = clone.getAttribute('srcset');
          if (srcset) clone.removeAttribute('srcset');
          clone.classList.add('pdp-gallery__img');
        }
        heroContent.replaceWith(clone);
        hero.dataset.index = String(idx);
      }
    });
  }

  /* ───────────────────────────────────────────────────────────
     LIGHTBOX — click any gallery cell, arrow keys, escape
  ──────────────────────────────────────────────────────────── */
  function initLightbox() {
    var lightbox = document.getElementById('pdp-lightbox');
    if (!lightbox) return;

    // (Re)bind gallery cell triggers on every call. The gallery is replaced on a
    // pack switch, so freshly-rendered cells need wiring — they all call the ONE
    // shared opener (lightbox._lbShow), bound once below. Keeping show/hide and
    // current/lastFocused as a single instance (not a fresh closure per call) is
    // what lets focus-restore + arrow-nav keep working after a pack switch.
    var gallery = document.querySelector('.pdp-gallery');
    if (gallery) {
      gallery.querySelectorAll('.pdp-gallery__cell').forEach(function (cell) {
        if (cell.dataset.lightboxBound === 'true') return;
        cell.dataset.lightboxBound = 'true';
        cell.addEventListener('click', function (e) {
          if (cell.classList.contains('pdp-gallery__thumb') && e.target.closest('.pdp-gallery__thumb') !== cell) return;
          if (lightbox._lbShow) lightbox._lbShow(parseInt(cell.dataset.index || '0', 10));
        });
        cell.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (lightbox._lbShow) lightbox._lbShow(parseInt(cell.dataset.index || '0', 10));
          }
        });
      });
    }

    if (lightbox.dataset.bound === 'true') return;
    lightbox.dataset.bound = 'true';

    var lbImg = lightbox.querySelector('.pdp-lightbox__img');
    var lbCounter = lightbox.querySelector('.pdp-lightbox__counter');
    var current = 0;
    var lastFocused = null;

    function imageList() {
      var g = document.querySelector('.pdp-gallery');
      if (!g) return [];
      var images = [];
      g.querySelectorAll('.pdp-gallery__thumb').forEach(function (c) {
        var img = c.querySelector('img');
        images.push(img ? img.src.replace(/width=\d+/, 'width=2048') : '');
      });
      return images;
    }

    function show(index) {
      var images = imageList();
      if (!images.length) return;
      var wasHidden = lightbox.getAttribute('aria-hidden') !== 'false';
      current = (index + images.length) % images.length;
      var src = images[current];
      if (src) {
        lbImg.src = src;
        lbImg.style.display = '';
      } else {
        lbImg.removeAttribute('src');
        lbImg.style.display = 'none';
      }
      if (lbCounter) lbCounter.textContent = (current + 1) + ' / ' + images.length;
      lightbox.setAttribute('aria-hidden', 'false');
      lightbox.inert = false;
      document.body.style.overflow = 'hidden';
      // On open (not on prev/next nav), remember the trigger and move focus
      // into the dialog so keyboard/SR users land on the close control.
      if (wasHidden) {
        lastFocused = document.activeElement;
        var closeBtn = lightbox.querySelector('.pdp-lightbox__close');
        if (closeBtn) closeBtn.focus();
      }
    }

    function hide() {
      lightbox.setAttribute('aria-hidden', 'true');
      lightbox.inert = true;
      document.body.style.overflow = '';
      // Return focus to the gallery cell that opened the dialog.
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
        lastFocused = null;
      }
    }

    lightbox._lbShow = show;

    var close = lightbox.querySelector('.pdp-lightbox__close');
    var prev = lightbox.querySelector('.pdp-lightbox__prev');
    var next = lightbox.querySelector('.pdp-lightbox__next');
    if (close) close.addEventListener('click', hide);
    if (prev) prev.addEventListener('click', function () { show(current - 1); });
    if (next) next.addEventListener('click', function () { show(current + 1); });
    lightbox.addEventListener('click', function (e) { if (e.target === lightbox) hide(); });
    document.addEventListener('keydown', function (e) {
      if (lightbox.getAttribute('aria-hidden') === 'true') return;
      if (e.key === 'Escape') return hide();
      if (e.key === 'ArrowLeft') return show(current - 1);
      if (e.key === 'ArrowRight') return show(current + 1);
      // Trap Tab within the dialog so focus can't escape to the page behind it.
      if (e.key === 'Tab') {
        var f = [];
        lightbox.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])').forEach(function (el) {
          if (!el.disabled && el.offsetParent !== null) f.push(el);
        });
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (!lightbox.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
        else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }

  /* ───────────────────────────────────────────────────────────
     STICKY BAR — visible when buy box leaves viewport, hidden when
     Final CTA enters viewport. Mobile: expand on tap (PDP-M1).
  ──────────────────────────────────────────────────────────── */
  var stickyBarVisible = false;

  function initStickyBar() {
    var bar = document.querySelector('.pdp-sticky-bar');
    if (!bar) return;

    /* Visibility: tied to buy-box bottom + final-cta entry. */
    function updateVisibility() {
      var buybox = document.querySelector('.pdp-buybox');
      var finalCta = document.querySelector('.final-cta, .dark-band');
      if (!buybox) return;
      var buyboxBottom = buybox.getBoundingClientRect().bottom;
      var finalTop = finalCta ? finalCta.getBoundingClientRect().top : Infinity;
      var buyboxOut = buyboxBottom < 80;
      var finalIn = finalTop < window.innerHeight - 100;
      var shouldShow = buyboxOut && !finalIn;
      stickyBarVisible = shouldShow;
      bar.classList.toggle('pdp-sticky-bar--visible', shouldShow);
      bar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      bar.inert = !shouldShow;
      if (!shouldShow && bar.classList.contains('is-expanded')) {
        bar.classList.remove('is-expanded');
      }
    }

    /* Mobile expand-on-tap chevron */
    if (bar.dataset.stickyBound !== 'true') {
      bar.dataset.stickyBound = 'true';
      bar.addEventListener('click', function (e) {
        var chev = e.target.closest('[data-sticky-toggle]');
        if (chev) {
          e.preventDefault();
          bar.classList.toggle('is-expanded');
          var indicator = bar.querySelector('[data-sticky-chevron]');
          if (indicator) indicator.textContent = bar.classList.contains('is-expanded') ? '▾' : '▴';
          return;
        }

        var packBtn = e.target.closest('[data-sticky-pack-btn]');
        if (packBtn) {
          /* Trigger the buy-box pack tab to flow through SRA */
          var handle = packBtn.dataset.stickyPackBtn;
          var heroTab = document.querySelector('.pdp-pack-tab[data-pack-handle="' + handle + '"]');
          if (heroTab) heroTab.click();
          return;
        }

        var optBtn = e.target.closest('[data-sticky-option]');
        if (optBtn) {
          var card = document.querySelector('[data-pdp-sub-disclose]');
          if (!card) return;
          var wantSub = optBtn.dataset.stickyOption === 'subscribe';
          var isSub = card.dataset.state === 'subscribed';
          if (wantSub !== isSub) {
            var toggle = card.querySelector('[data-pdp-sub-toggle]');
            if (toggle) toggle.click();
          }
          return;
        }

        var atc = e.target.closest('[data-sticky-atc]');
        if (atc) {
          var form = document.getElementById('pdp-product-form');
          if (form) form.requestSubmit();
        }
      });

      /* Roving arrow-key nav for the sticky pack radiogroup (mirrors the main
         switcher — selection does NOT follow focus; Space/Enter/click selects). */
      bar.addEventListener('keydown', function (e) {
        var packBtn = e.target.closest('[data-sticky-pack-btn]');
        if (packBtn) radioGroupArrows(e, packBtn.closest('.pdp-sticky-bar__pack'), '[data-sticky-pack-btn]');
      });
    }

    /* Bind scroll/resize once */
    if (!window.__pdpStickyScrollBound) {
      window.__pdpStickyScrollBound = true;
      window.addEventListener('scroll', updateVisibility, { passive: true });
      window.addEventListener('resize', updateVisibility, { passive: true });
    }
    updateVisibility();
    syncStickyBar();
  }

  /* ───────────────────────────────────────────────────────────
     syncStickyBar — mirror buy-box state into sticky-bar surfaces
  ──────────────────────────────────────────────────────────── */
  function syncStickyBar() {
    var bar = document.querySelector('.pdp-sticky-bar');
    if (!bar) return;

    var card = document.querySelector('[data-pdp-sub-disclose]');
    var isSub = card && card.dataset.state === 'subscribed';
    var priceEl = document.querySelector('[data-pdp-price]');
    var priceText = priceEl ? priceEl.textContent.trim() : '';

    /* When subscribed, mirror the discounted sub price (server-rendered exactly
       in the disclosure's [data-pdp-sub-price]); otherwise the one-time price. */
    var displayPrice = priceText;
    if (isSub && card) {
      var subEl = card.querySelector('[data-pdp-sub-price]');
      displayPrice = subEl ? subEl.textContent.trim() : priceText;
    }

    /* Title — copy from buy-box title */
    var titleEl = document.querySelector('.pdp-buybox__title');
    var stickyName = bar.querySelector('.pdp-sticky-bar__name');
    if (titleEl && stickyName) stickyName.textContent = titleEl.textContent.trim();

    /* Main price */
    var stickyPrice = bar.querySelector('[data-sticky-price]');
    if (stickyPrice) {
      stickyPrice.textContent = displayPrice;
      stickyPrice.classList.toggle('pdp-sticky-bar__price--gold', isSub);
    }

    /* Meta line */
    var stickyMeta = bar.querySelector('[data-sticky-meta]');
    if (stickyMeta) {
      /* The saving is worked out in Liquid from real prices (subscription-save-phrase)
         and carried on the card, so a 0% November plan never reads "save 10%". */
      var subPhrase = card ? (card.dataset.subPhrase || '') : '';
      stickyMeta.textContent = isSub ? ('Subscribing' + (subPhrase ? ' · ' + subPhrase : '')) : 'One-time';
    }

    /* ATC label */
    var stickyAtc = bar.querySelector('[data-sticky-atc]');
    if (stickyAtc) stickyAtc.textContent = isSub ? 'Subscribe' : 'Add to Cart';

    /* Mini-buybox option button state */
    bar.querySelectorAll('[data-sticky-option]').forEach(function (btn) {
      btn.setAttribute('aria-pressed', String(btn.dataset.stickyOption === (isSub ? 'subscribe' : 'onetime')));
    });

    /* The expanded options always mirror the main box: the one-time price and
       the card's plan price and saving phrase (both server-rendered from the
       plan allocation). Unconditional, so a pack switch (15-Pack → 5-Pack)
       never leaves the old pack's subscribe price or wording behind. */
    var oneTimeOption = bar.querySelector('[data-sticky-option="onetime"] .pdp-sticky-bar__option-price');
    var subOption = bar.querySelector('[data-sticky-option="subscribe"] .pdp-sticky-bar__option-price');
    var subOptionLabel = bar.querySelector('[data-sticky-option="subscribe"] .pdp-sticky-bar__option-label');
    var planPrice = card && card.querySelector('[data-pdp-sub-price]');
    if (oneTimeOption && priceEl) oneTimeOption.textContent = priceEl.dataset.onetime || priceText;
    if (subOption && planPrice) subOption.textContent = planPrice.textContent.trim();
    if (subOptionLabel && card) {
      var optPhrase = card.dataset.subPhrase || '';
      subOptionLabel.textContent = 'Subscribe' + (optPhrase ? ' · ' + optPhrase : '');
    }

    /* Sticky pack-btn state mirrors current page. role=radio + aria-checked +
       roving tabindex, matching the main pack switcher (consistent SR semantics). */
    var currentHandle = document.querySelector('.pdp-hero')?.dataset.productHandle;
    bar.querySelectorAll('[data-sticky-pack-btn]').forEach(function (btn) {
      var sel = btn.dataset.stickyPackBtn === currentHandle;
      btn.setAttribute('aria-checked', String(sel));
      btn.setAttribute('tabindex', sel ? '0' : '-1');
    });
  }

  /* ───────────────────────────────────────────────────────────
     INIT
  ──────────────────────────────────────────────────────────── */
  function init() {
    initAccordions();
    initSubDisclosure();
    initVariantSwitcher();
    initGalleryHeroSwap();
    initLightbox();
    initPackSwitcher();
    initStickyBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
